import React from 'react';
import { Link } from 'react-router-dom';
import '../../admin/admin-panel.css';
import SiteContentContext from '../../content/SiteContentContext';
import { getByPath, getActiveLocale } from '../../content/siteContentUtils';
import { getAdminAuthState, setAdminAuthState, validateAdminCredentials } from '../../admin/adminAuth';
import { listFormSubmissions, deleteFormSubmission, clearFormSubmissions } from '../../services/formSubmissionsApi';

const tabs = [
  { id: 'slider', label: '1- Slider' },
  { id: 'about', label: '2- Hakkımızda' },
  { id: 'productCategories', label: '3- Ürün Kategorileri' },
  { id: 'numbers', label: '4- Rakamlarla Biz' },
  { id: 'testimonials', label: '5- Müşteri Yorumları' },
  { id: 'clients', label: '6- Müşterilerimiz' },
  { id: 'contact', label: '7- İletişim (Sosyal Medya Dahil)' },
  { id: 'formLeads', label: '8- Form Talepleri' },
];

const productCategoryConfigs = [
  { key: 'apartment', title: 'Daire Giriş Kapıları' },
  { key: 'villa', title: 'Villa Kapı Modelleri' },
  { key: 'building', title: 'Bina Giriş Kapıları' },
  { key: 'interior', title: 'İç Kapılar' },
];

const UPLOAD_API_URL = '/api/upload';

const Field = ({ label, children, className = '' }) => (
  <div className={`admin-field ${className}`.trim()}>
    <label>{label}</label>
    {children}
  </div>
);

const TextInput = ({ label, value, onChange, type = 'text', className = '' }) => (
  <Field label={label} className={className}>
    <input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
  </Field>
);

const TextArea = ({ label, value, onChange, className = '' }) => (
  <Field label={label} className={className}>
    <textarea value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
  </Field>
);

const FileToBase64Field = ({ label, onSelect, className = '', accept = 'image/*' }) => {
  const [isUploading, setIsUploading] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setError('');

    const requestUrl = `${UPLOAD_API_URL}?filename=${encodeURIComponent(file.name || 'image')}`;

    fetch(requestUrl, {
      method: 'POST',
      headers: {
        'content-type': file.type || 'application/octet-stream',
      },
      body: file,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload?.ok) {
          throw new Error(payload?.message || 'Upload başarısız');
        }
        onSelect(payload.url || '');
      })
      .catch((uploadError) => {
        setError(uploadError?.message || 'Upload başarısız. Vercel deploy veya /api endpoint çalışıyor olmalı.');
      })
      .finally(() => {
        setIsUploading(false);
        event.target.value = '';
      });
  };

  return (
    <Field label={label} className={className}>
      <input type="file" accept={accept} onChange={handleChange} />
      {isUploading ? <div className="admin-hint">Yükleniyor...</div> : null}
      {error ? <div className="admin-error">{error}</div> : null}
    </Field>
  );
};

const StringListEditor = ({ label, values, onChange }) => {
  const list = Array.isArray(values) ? values : [];
  const rows = list.length ? list : [''];

  return (
    <div className="admin-section">
      <h3>{label}</h3>
      <div className="admin-form-grid">
        {rows.map((item, index) => (
          <TextInput
            key={`${label}-${index}`}
            label={`${label} ${index + 1}`}
            value={item}
            onChange={(nextValue) => {
              const next = [...rows];
              next[index] = nextValue;
              onChange(next);
            }}
            className="full"
          />
        ))}
      </div>
      <div className="admin-actions">
        <button type="button" className="admin-btn light" onClick={() => onChange([...(list || []), ''])}>Satır Ekle</button>
        {list.length > 1 ? (
          <button type="button" className="admin-btn light" onClick={() => onChange(list.slice(0, -1))}>Son Satırı Sil</button>
        ) : null}
      </div>
    </div>
  );
};

const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateAdminCredentials(username, password)) {
      setError('Kullanıcı adı veya şifre hatalı.');
      return;
    }

    setAdminAuthState(true);
    onLogin(true);
  };

  return (
    <div className="admin-panel">
      <div className="admin-shell admin-login-wrap">
        <div className="admin-card">
          <h1 className="admin-title">Yönetim Paneli</h1>
          <p className="admin-subtitle">İçerik yönetimi (localStorage + opsiyonel bulut senkron)</p>
          <form onSubmit={handleSubmit}>
            <TextInput label="Kullanıcı Adı" value={username} onChange={setUsername} />
            <TextInput label="Şifre" value={password} onChange={setPassword} type="password" />
            <div className="admin-actions">
              <button type="submit" className="admin-btn primary">Giriş Yap</button>
              <Link to="/" className="admin-btn light" style={{ textDecoration: 'none' }}>Siteye Dön</Link>
            </div>
            <div className="admin-hint" style={{ marginTop: 10 }}>
              Varsayılan giriş: <code>admin</code> / <code>123456</code>
            </div>
            {error ? <div className="admin-error">{error}</div> : null}
          </form>
        </div>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const { content, setContentAtPath, replaceContent, resetContent, remoteStorageMode } = React.useContext(SiteContentContext);
  const [isAuthed, setIsAuthed] = React.useState(() => getAdminAuthState());
  const [activeTab, setActiveTab] = React.useState('slider');
  const [aboutAdminTab, setAboutAdminTab] = React.useState('general');
  const [productCategoryAdminTab, setProductCategoryAdminTab] = React.useState(productCategoryConfigs[0].key);
  const [productEditor, setProductEditor] = React.useState({
    isOpen: false,
    mode: 'add',
    categoryKey: productCategoryConfigs[0].key,
    index: -1,
    draft: { imageUrl: '', imageUrls: [], name: '', code: '', description: '' },
  });
  const [message, setMessage] = React.useState('');
  const [jsonDraft, setJsonDraft] = React.useState('');
  const [showJsonTools, setShowJsonTools] = React.useState(false);
  const [editorLocale, setEditorLocale] = React.useState(() => getActiveLocale(content || {}));
  const [formLeads, setFormLeads] = React.useState([]);
  const [isFormLeadsLoading, setIsFormLeadsLoading] = React.useState(false);
  const [formLeadsError, setFormLeadsError] = React.useState('');
  const [openSliderIndex, setOpenSliderIndex] = React.useState(0);
  const isBlobSyncEnabled = remoteStorageMode === 'blob';

  React.useEffect(() => {
    setJsonDraft(JSON.stringify(content, null, 2));
  }, [content]);

  React.useEffect(() => {
    setEditorLocale((prev) => prev || getActiveLocale(content || {}));
  }, [content]);

  React.useEffect(() => {
    if (aboutAdminTab === 'categories' || aboutAdminTab === 'achievements') {
      setAboutAdminTab('general');
    }
  }, [aboutAdminTab]);

  React.useEffect(() => {
    const hasCategory = productCategoryConfigs.some((item) => item.key === productCategoryAdminTab);
    if (!hasCategory) {
      setProductCategoryAdminTab(productCategoryConfigs[0].key);
    }
  }, [productCategoryAdminTab]);

  const loadFormLeads = React.useCallback(async () => {
    setIsFormLeadsLoading(true);
    setFormLeadsError('');
    try {
      const rows = await listFormSubmissions();
      setFormLeads(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setFormLeads([]);
      setFormLeadsError(error?.message || 'Form kayıtları alınamadı.');
    } finally {
      setIsFormLeadsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (isAuthed && activeTab === 'formLeads') {
      loadFormLeads();
    }
  }, [isAuthed, activeTab, loadFormLeads]);

  if (!isAuthed) {
    return <AdminLogin onLogin={setIsAuthed} />;
  }

  const langBase = `translations.${editorLocale}`;

  const setPath = (path, value) => {
    setContentAtPath(path, value);
    setMessage(
      isBlobSyncEnabled
        ? 'Kaydedildi (cihaz + bulut senkron).'
        : 'Kaydedildi (bu cihaz). Kalici bulut senkronu icin Vercel Blob token gerekir.'
    );
  };

  const normalizeProductImageUrls = (item) => {
    const list = Array.isArray(item?.imageUrls) ? item.imageUrls : [];
    const cleaned = list
      .map((value) => (typeof value === 'string' ? value.trim() : ''))
      .filter(Boolean);
    const fallbackImage = typeof item?.imageUrl === 'string' ? item.imageUrl.trim() : '';

    if (!cleaned.length && fallbackImage) {
      return [fallbackImage];
    }

    return cleaned;
  };

  const normalizeProduct = (item) => {
    const imageUrls = normalizeProductImageUrls(item);
    return {
      imageUrl: imageUrls[0] || '',
      imageUrls,
      name: item?.name || '',
      code: item?.code || '',
      description: item?.description || '',
    };
  };

  const getCategoryProducts = (categoryKey) => {
    const rows = getByPath(content, `productCategories.${categoryKey}.items`, []);
    return Array.isArray(rows) ? rows.map((item) => normalizeProduct(item)) : [];
  };

  const setCategoryProducts = (categoryKey, rows) => {
    setPath(`productCategories.${categoryKey}.items`, rows.map((item) => normalizeProduct(item)));
  };

  const openNewProductModal = (categoryKey) => {
    setProductCategoryAdminTab(categoryKey);
    setProductEditor({
      isOpen: true,
      mode: 'add',
      categoryKey,
      index: -1,
      draft: normalizeProduct({}),
    });
  };

  const openEditProductModal = (categoryKey, index) => {
    const rows = getCategoryProducts(categoryKey);
    if (!rows[index]) return;
    setProductEditor({
      isOpen: true,
      mode: 'edit',
      categoryKey,
      index,
      draft: normalizeProduct(rows[index]),
    });
  };

  const closeProductModal = () => {
    setProductEditor((prev) => ({ ...prev, isOpen: false }));
  };

  const setProductEditorField = (field, value) => {
    setProductEditor((prev) => ({
      ...prev,
      draft: {
        ...prev.draft,
        [field]: value,
      },
    }));
  };

  const setProductEditorImageAt = (index, value) => {
    setProductEditor((prev) => {
      const rows = Array.isArray(prev.draft.imageUrls) ? [...prev.draft.imageUrls] : [];
      while (rows.length <= index) {
        rows.push('');
      }
      rows[index] = value;
      const firstImage = rows.find((row) => typeof row === 'string' && row.trim()) || '';

      return {
        ...prev,
        draft: {
          ...prev.draft,
          imageUrls: rows,
          imageUrl: firstImage,
        },
      };
    });
  };

  const addProductEditorImageRow = () => {
    setProductEditor((prev) => ({
      ...prev,
      draft: {
        ...prev.draft,
        imageUrls: [...(Array.isArray(prev.draft.imageUrls) ? prev.draft.imageUrls : []), ''],
      },
    }));
  };

  const removeProductEditorImageRow = (index) => {
    setProductEditor((prev) => {
      const currentRows = Array.isArray(prev.draft.imageUrls) ? prev.draft.imageUrls : [];
      const nextRows = currentRows.filter((_, rowIndex) => rowIndex !== index);
      const safeRows = nextRows.length ? nextRows : [''];
      const firstImage = safeRows.find((row) => typeof row === 'string' && row.trim()) || '';

      return {
        ...prev,
        draft: {
          ...prev.draft,
          imageUrls: safeRows,
          imageUrl: firstImage,
        },
      };
    });
  };

  const removeProductFromCategory = (categoryKey, index) => {
    const rows = getCategoryProducts(categoryKey);
    if (!rows[index]) return;
    const shouldDelete = window.confirm('Bu ürünü silmek istediğinize emin misiniz?');
    if (!shouldDelete) return;
    setCategoryProducts(categoryKey, rows.filter((_, rowIndex) => rowIndex !== index));
  };

  const moveProductInCategory = (categoryKey, fromIndex, toIndex) => {
    const rows = getCategoryProducts(categoryKey);
    if (!rows[fromIndex]) return;
    if (toIndex < 0 || toIndex >= rows.length) return;

    const nextRows = [...rows];
    const [movedProduct] = nextRows.splice(fromIndex, 1);
    nextRows.splice(toIndex, 0, movedProduct);
    setCategoryProducts(categoryKey, nextRows);
  };

  const saveProductModal = () => {
    const categoryKey = productEditor.categoryKey;
    const rows = getCategoryProducts(categoryKey);
    const nextProduct = normalizeProduct(productEditor.draft);

    if (productEditor.mode === 'edit' && rows[productEditor.index]) {
      rows[productEditor.index] = nextProduct;
      setCategoryProducts(categoryKey, rows);
      closeProductModal();
      return;
    }

    setCategoryProducts(categoryKey, [...rows, nextProduct]);
    closeProductModal();
  };

  const getLang = (path, fallback = '') => getByPath(content, `${langBase}.${path}`, fallback);
  const setLang = (path, value) => setPath(`${langBase}.${path}`, value);

  const handleLogout = () => {
    setAdminAuthState(false);
    setIsAuthed(false);
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'site-content.json';
    link.click();
    window.URL.revokeObjectURL(url);
    setMessage('JSON dışa aktarıldı.');
  };

  const handleImport = () => {
    try {
      const parsed = JSON.parse(jsonDraft);
      replaceContent(parsed);
      setMessage('JSON içe aktarıldı.');
    } catch (error) {
      setMessage('JSON formatı geçersiz.');
    }
  };

  const handleReset = () => {
    resetContent();
    setMessage('Varsayılan içerik geri yüklendi.');
  };

  const getSliderRows = () => {
    const mediaSlides = getByPath(content, 'home.slider.slides', []);
    const textSlides = getByPath(content, `${langBase}.pages.home.slider.slides`, []);
    const length = Math.max(mediaSlides.length || 0, textSlides.length || 0, 1);

    return Array.from({ length }).map((_, index) => ({
      imageUrl: getByPath(mediaSlides, `${index}.imageUrl`, ''),
      mobileImageUrl: getByPath(mediaSlides, `${index}.mobileImageUrl`, ''),
      buttonUrl: getByPath(mediaSlides, `${index}.buttonUrl`, '#'),
      tagLine: getByPath(textSlides, `${index}.tagLine`, ''),
      title: getByPath(textSlides, `${index}.title`, ''),
      description: getByPath(textSlides, `${index}.description`, ''),
      buttonLabel: getByPath(textSlides, `${index}.buttonLabel`, ''),
    }));
  };

  const setSliderRows = (rows) => {
    const normalized = rows.map((row) => ({
      imageUrl: row.imageUrl || '',
      mobileImageUrl: row.mobileImageUrl || '',
      buttonUrl: row.buttonUrl || '#',
      tagLine: row.tagLine || '',
      title: row.title || '',
      description: row.description || '',
      buttonLabel: row.buttonLabel || '',
    }));

    setPath(
      'home.slider.slides',
      normalized.map((row) => ({
        imageUrl: row.imageUrl,
        mobileImageUrl: row.mobileImageUrl,
        buttonUrl: row.buttonUrl,
      }))
    );

    setPath(
      `${langBase}.pages.home.slider.slides`,
      normalized.map((row) => ({
        tagLine: row.tagLine,
        title: row.title,
        description: row.description,
        buttonLabel: row.buttonLabel,
      }))
    );
  };

  const renderSliderTab = () => {
    const sliderRows = getSliderRows();

    const updateSliderRow = (index, key, value) => {
      const nextRows = sliderRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row
      );
      setSliderRows(nextRows);
    };

    const addSlider = () => {
      const nextOpenIndex = sliderRows.length;
      setSliderRows([
        ...sliderRows,
        {
          imageUrl: '',
          mobileImageUrl: '',
          buttonUrl: '#',
          tagLine: '',
          title: '',
          description: '',
          buttonLabel: 'Daha Fazla Oku',
        },
      ]);
      setOpenSliderIndex(nextOpenIndex);
    };

    const removeSlider = (index) => {
      if (sliderRows.length <= 1) {
        return;
      }
      const nextRows = sliderRows.filter((_, rowIndex) => rowIndex !== index);
      setSliderRows(nextRows);
      setOpenSliderIndex((prev) => {
        if (!nextRows.length) return -1;
        if (prev === index) return Math.min(index, nextRows.length - 1);
        if (prev > index) return prev - 1;
        return prev;
      });
    };

    return (
      <>
        <div className="admin-section">
          <h3>Ana Sayfa Slider Alanı</h3>
          <div className="admin-hint">
            Sadece ana sayfa hero slider&apos;ındaki alanlar yer alır. Düzenleme dili: <strong>{editorLocale.toUpperCase()}</strong>
          </div>
        </div>

        {sliderRows.map((slide, index) => (
          <div className={`admin-section admin-repeat-card admin-slider-card ${openSliderIndex === index ? 'open' : ''}`.trim()} key={`slider-row-${index}`}>
            <div className="admin-repeat-head admin-slider-head">
              <div className="admin-slider-summary">
                <h3>Slider {index + 1}</h3>
                <div className="admin-slider-meta">
                  <span className="admin-slider-title-preview">{(slide.title || 'Başlık girilmemiş').trim()}</span>
                  <span className="admin-slider-chip">{(slide.imageUrl || '').trim() ? 'Masaüstü: Var' : 'Masaüstü: Yok'}</span>
                  <span className="admin-slider-chip">{(slide.mobileImageUrl || '').trim() ? 'Mobil: Var' : 'Mobil: Yok'}</span>
                </div>
              </div>
              <div className="admin-actions">
                <button
                  type="button"
                  className="admin-btn secondary"
                  onClick={() => setOpenSliderIndex((prev) => (prev === index ? -1 : index))}
                >
                  {openSliderIndex === index ? 'Detayı Kapat' : 'Detayı Aç'}
                </button>
                <button type="button" className="admin-btn light" onClick={() => removeSlider(index)} disabled={sliderRows.length <= 1}>
                  Sliderı Sil
                </button>
              </div>
            </div>

            {openSliderIndex === index ? (
              <div className="admin-form-grid admin-slider-grid">
                <TextInput
                  label="Masaüstü Görsel URL (/uploads/... otomatik gelir)"
                  value={slide.imageUrl}
                  onChange={(v) => updateSliderRow(index, 'imageUrl', v)}
                />
                <FileToBase64Field
                  label="Masaüstü Görsel Yükle"
                  onSelect={(v) => updateSliderRow(index, 'imageUrl', v)}
                />
                <TextInput
                  label="Mobil Görsel URL (Boşsa masaüstü görseli kullanılır)"
                  value={slide.mobileImageUrl}
                  onChange={(v) => updateSliderRow(index, 'mobileImageUrl', v)}
                />
                <FileToBase64Field
                  label="Mobil Görsel Yükle"
                  onSelect={(v) => updateSliderRow(index, 'mobileImageUrl', v)}
                />
                <TextInput
                  label="Üst Küçük Başlık"
                  value={slide.tagLine}
                  onChange={(v) => updateSliderRow(index, 'tagLine', v)}
                />
                <TextInput
                  label="Buton Metni"
                  value={slide.buttonLabel}
                  onChange={(v) => updateSliderRow(index, 'buttonLabel', v)}
                />
                <TextInput
                  label="Buton Linki"
                  value={slide.buttonUrl}
                  onChange={(v) => updateSliderRow(index, 'buttonUrl', v)}
                />
                <div />
                <TextArea
                  label="Ana Başlık"
                  value={slide.title}
                  onChange={(v) => updateSliderRow(index, 'title', v)}
                  className="full"
                />
                <TextArea
                  label="Açıklama Metni"
                  value={slide.description}
                  onChange={(v) => updateSliderRow(index, 'description', v)}
                  className="full"
                />
              </div>
            ) : null}
          </div>
        ))}

        <div className="admin-actions">
          <button type="button" className="admin-btn primary" onClick={addSlider}>Yeni Slider Ekle</button>
        </div>
      </>
    );
  };

  const renderAboutGeneralTab = () => (
    <>
      <div className="admin-section">
        <h3>Hakkımızda Banner</h3>
        <div className="admin-form-grid">
          <TextInput label="Başlık" value={getLang('pages.about.banner.title', '')} onChange={(v) => setLang('pages.about.banner.title', v)} />
          <TextInput label="Breadcrumb Başlığı" value={getLang('pages.about.banner.pageName', '')} onChange={(v) => setLang('pages.about.banner.pageName', v)} />
          <TextArea label="Açıklama" value={getLang('pages.about.banner.description', '')} onChange={(v) => setLang('pages.about.banner.description', v)} className="full" />
          <TextInput label="Banner Görseli URL" value={getByPath(content, 'media.pages.aboutBanner', '')} onChange={(v) => setPath('media.pages.aboutBanner', v)} className="full" />
          <FileToBase64Field label="Banner Görseli Yükle" onSelect={(v) => setPath('media.pages.aboutBanner', v)} className="full" />
        </div>
      </div>

      <div className="admin-section">
        <h3>Hakkımızda İçerik Kutusu</h3>
        <div className="admin-form-grid">
          <TextInput label="Sol Görsel URL" value={getByPath(content, 'media.pages.aboutIntroImage', '')} onChange={(v) => setPath('media.pages.aboutIntroImage', v)} className="full" />
          <FileToBase64Field label="Sol Görsel Yükle" onSelect={(v) => setPath('media.pages.aboutIntroImage', v)} className="full" />
          <TextArea label="Başlık" value={getLang('pages.about.intro.title', '')} onChange={(v) => setLang('pages.about.intro.title', v)} className="full" />
          <TextArea label="Açıklama" value={getLang('pages.about.intro.description', '')} onChange={(v) => setLang('pages.about.intro.description', v)} className="full" />
        </div>
      </div>

      <div className="admin-section">
        <h3>Anasayfa Hakkımızda Görselleri</h3>
        <div className="admin-hint">Ana sayfadaki Hakkımızda alanının sağındaki slider ve arkadaki sabit görsel.</div>
        {Array.from({ length: 4 }).map((_, index) => (
          <div className="admin-form-grid" key={`home-about-slider-image-${index}`} style={{ marginBottom: 10 }}>
            <TextInput
              label={`Slider Görsel ${index + 1} URL`}
              value={getByPath(content, `media.pages.homeAboutSliderImages.${index}.imageUrl`, '')}
              onChange={(v) => setPath(`media.pages.homeAboutSliderImages.${index}.imageUrl`, v)}
              className="full"
            />
            <FileToBase64Field
              label={`Slider Görsel ${index + 1} Yükle`}
              onSelect={(v) => setPath(`media.pages.homeAboutSliderImages.${index}.imageUrl`, v)}
              className="full"
            />
          </div>
        ))}
        <div className="admin-form-grid">
          <TextInput
            label="Arka Sabit Görsel URL"
            value={getByPath(content, 'media.pages.homeAboutBeforeImage', '')}
            onChange={(v) => setPath('media.pages.homeAboutBeforeImage', v)}
            className="full"
          />
          <FileToBase64Field
            label="Arka Sabit Görsel Yükle"
            onSelect={(v) => setPath('media.pages.homeAboutBeforeImage', v)}
            className="full"
          />
        </div>
      </div>
    </>
  );

  const renderAboutWhatWeDoTab = () => {
    const itemCount = 3;

    return (
      <>
        <div className="admin-section">
          <h3>Neler Yapıyoruz</h3>
          <div className="admin-form-grid">
            <TextInput label="Bölüm Başlığı" value={getLang('pages.about.whatWeDo.title', '')} onChange={(v) => setLang('pages.about.whatWeDo.title', v)} className="full" />
            <TextInput label="Sağ Arka Plan Görseli URL" value={getByPath(content, 'media.pages.aboutWhatWeDoRightImage', '')} onChange={(v) => setPath('media.pages.aboutWhatWeDoRightImage', v)} className="full" />
            <FileToBase64Field label="Sağ Arka Plan Görseli Yükle" onSelect={(v) => setPath('media.pages.aboutWhatWeDoRightImage', v)} className="full" />
          </div>
        </div>

        {Array.from({ length: itemCount }).map((_, index) => (
          <div className="admin-section admin-repeat-card" key={`what-we-do-admin-${index}`}>
            <div className="admin-repeat-head">
              <h3>Kart {index + 1}</h3>
            </div>
            <div className="admin-form-grid">
              <TextInput label="Kart Görsel URL" value={getByPath(content, `media.pages.aboutWhatWeDoCards.${index}.imageUrl`, '')} onChange={(v) => setPath(`media.pages.aboutWhatWeDoCards.${index}.imageUrl`, v)} className="full" />
              <FileToBase64Field label="Kart Görsel Yükle" onSelect={(v) => setPath(`media.pages.aboutWhatWeDoCards.${index}.imageUrl`, v)} className="full" />
              <TextInput label="Sıra No" value={getLang(`pages.about.whatWeDo.items.${index}.number`, '')} onChange={(v) => setLang(`pages.about.whatWeDo.items.${index}.number`, v)} />
              <TextInput label="Başlık" value={getLang(`pages.about.whatWeDo.items.${index}.title`, '')} onChange={(v) => setLang(`pages.about.whatWeDo.items.${index}.title`, v)} />
            </div>
          </div>
        ))}
      </>
    );
  };

  const renderAboutDocumentsTab = () => {
    const textItems = Array.isArray(getByPath(content, `${langBase}.pages.about.documents.items`, []))
      ? getByPath(content, `${langBase}.pages.about.documents.items`, [])
      : [];
    const mediaItems = Array.isArray(getByPath(content, 'media.pages.aboutDocuments', []))
      ? getByPath(content, 'media.pages.aboutDocuments', [])
      : [];

    const rowCount = Math.max(textItems.length, mediaItems.length, 1);
    const rows = Array.from({ length: rowCount }).map((_, index) => ({
      title: getByPath(textItems, `${index}.title`, ''),
      description: getByPath(textItems, `${index}.description`, ''),
      fileUrl: getByPath(mediaItems, `${index}.fileUrl`, ''),
    }));

    const updateRows = (nextRows) => {
      setLang(
        'pages.about.documents.items',
        nextRows.map((row) => ({
          title: row?.title || '',
          description: row?.description || '',
        }))
      );

      setPath(
        'media.pages.aboutDocuments',
        nextRows.map((row) => ({
          fileUrl: row?.fileUrl || '',
        }))
      );
    };

    return (
      <>
        <div className="admin-section">
          <h3>Belgelerimiz</h3>
          <div className="admin-hint">Hakkımızda sayfasındaki belge kartlarını ve PDF dosyalarını yönetir.</div>
          <div className="admin-form-grid">
            <TextInput label="Bölüm Başlığı" value={getLang('pages.about.documents.title', '')} onChange={(v) => setLang('pages.about.documents.title', v)} className="full" />
            <TextArea label="Bölüm Açıklaması" value={getLang('pages.about.documents.description', '')} onChange={(v) => setLang('pages.about.documents.description', v)} className="full" />
            <TextInput label="Buton Yazısı (Dosya Varsa)" value={getLang('pages.about.documents.openLabel', '')} onChange={(v) => setLang('pages.about.documents.openLabel', v)} />
            <TextInput label="Buton Yazısı (Dosya Yoksa)" value={getLang('pages.about.documents.missingLabel', '')} onChange={(v) => setLang('pages.about.documents.missingLabel', v)} />
          </div>
        </div>

        {rows.map((row, index) => (
          <div className="admin-section admin-repeat-card" key={`about-doc-admin-${index}`}>
            <div className="admin-repeat-head">
              <h3>Belge {index + 1}</h3>
              <div className="admin-actions">
                <button
                  type="button"
                  className="admin-btn light"
                  onClick={() => {
                    if (rows.length <= 1) return;
                    updateRows(rows.filter((_, rowIndex) => rowIndex !== index));
                  }}
                  disabled={rows.length <= 1}
                >
                  Belgeyi Sil
                </button>
              </div>
            </div>
            <div className="admin-form-grid">
              <TextInput
                label="Belge Başlığı"
                value={row.title}
                onChange={(v) => {
                  const next = [...rows];
                  next[index] = { ...next[index], title: v };
                  updateRows(next);
                }}
                className="full"
              />
              <TextArea
                label="Belge Açıklaması"
                value={row.description}
                onChange={(v) => {
                  const next = [...rows];
                  next[index] = { ...next[index], description: v };
                  updateRows(next);
                }}
                className="full"
              />
              <TextInput
                label="PDF URL"
                value={row.fileUrl}
                onChange={(v) => {
                  const next = [...rows];
                  next[index] = { ...next[index], fileUrl: v };
                  updateRows(next);
                }}
                className="full"
              />
              <FileToBase64Field
                label="PDF Yükle"
                accept=".pdf,application/pdf"
                onSelect={(v) => {
                  const next = [...rows];
                  next[index] = { ...next[index], fileUrl: v };
                  updateRows(next);
                }}
                className="full"
              />
            </div>
          </div>
        ))}

        <div className="admin-actions">
          <button
            type="button"
            className="admin-btn primary"
            onClick={() => updateRows([...(rows || []), { title: '', description: '', fileUrl: '' }])}
          >
            Yeni Belge Ekle
          </button>
        </div>
      </>
    );
  };

  const renderAboutServicesTab = () => {
    const serviceCount = 6;

    return (
      <>
        <div className="admin-section">
          <h3>Tüm Hizmetler (Ana Sayfa)</h3>
          <div className="admin-hint">Ana sayfadaki “Tüm Hizmetler” alanını yönetir. İkon için örnek: <code>fa fa-wrench</code></div>
          <div className="admin-form-grid">
            <TextInput label="Bölüm Başlığı" value={getLang('pages.home.services.title', '')} onChange={(v) => setLang('pages.home.services.title', v)} />
            <TextInput label="Alt Büyük Yazı" value={getLang('pages.home.services.highlightText', '')} onChange={(v) => setLang('pages.home.services.highlightText', v)} />
          </div>
        </div>

        {Array.from({ length: serviceCount }).map((_, index) => (
          <div className="admin-section admin-repeat-card" key={`home-service-admin-${index}`}>
            <div className="admin-repeat-head">
              <h3>Hizmet Kartı {index + 1}</h3>
            </div>
            <div className="admin-form-grid">
              <TextInput label="Sıra No" value={getLang(`pages.home.services.items.${index}.count`, '')} onChange={(v) => setLang(`pages.home.services.items.${index}.count`, v)} />
              <TextInput label="İkon Class (fa fa-...)" value={getByPath(content, `home.services.items.${index}.iconClass`, '')} onChange={(v) => setPath(`home.services.items.${index}.iconClass`, v)} />
              <TextInput label="Başlık" value={getLang(`pages.home.services.items.${index}.title`, '')} onChange={(v) => setLang(`pages.home.services.items.${index}.title`, v)} className="full" />
              <TextArea label="Açıklama" value={getLang(`pages.home.services.items.${index}.description`, '')} onChange={(v) => setLang(`pages.home.services.items.${index}.description`, v)} className="full" />
            </div>
          </div>
        ))}
      </>
    );
  };

  const renderAboutProjectsTab = () => {
    const projectItems = Array.isArray(getByPath(content, 'home.projects.items', []))
      ? getByPath(content, 'home.projects.items', [])
      : [];
    const rows = projectItems.length ? projectItems : [{ imageUrl: '' }];

    const updateRows = (nextRows) => {
      setPath(
        'home.projects.items',
        nextRows.map((item, index) => ({
          ...(projectItems[index] || {}),
          imageUrl: typeof item === 'string' ? item : item?.imageUrl || '',
        }))
      );
    };

    return (
      <>
        <div className="admin-section">
          <h3>Referanslarımız (Ana Sayfa)</h3>
          <div className="admin-hint">Ana sayfada sadece logo gösterilen yatay kayan alanı yönetir.</div>
          <div className="admin-form-grid">
            <TextInput label="Bölüm Başlığı" value={getLang('pages.home.projects.title', '')} onChange={(v) => setLang('pages.home.projects.title', v)} />
            <div className="admin-hint full">Filtreler ve metin alanları kaldırıldı. Bu bölümde sadece logolar gösterilir.</div>
          </div>
        </div>

        {rows.map((item, index) => {
          const value = typeof item === 'string' ? item : item?.imageUrl || '';
          return (
          <div className="admin-section admin-repeat-card" key={`project-card-admin-${index}`}>
            <div className="admin-repeat-head">
              <h3>Logo {index + 1}</h3>
              <div className="admin-actions">
                <button
                  type="button"
                  className="admin-btn light"
                  onClick={() => {
                    if (rows.length <= 1) return;
                    updateRows(rows.filter((_, rowIndex) => rowIndex !== index));
                  }}
                  disabled={rows.length <= 1}
                >
                  Logoyu Sil
                </button>
              </div>
            </div>
            <div className="admin-form-grid">
              <TextInput
                label="Logo URL"
                value={value}
                onChange={(v) => {
                  const next = [...rows];
                  next[index] = {
                    ...(typeof next[index] === 'string' ? {} : next[index]),
                    imageUrl: v,
                  };
                  updateRows(next);
                }}
                className="full"
              />
              <FileToBase64Field
                label="Logo Yükle"
                onSelect={(v) => {
                  const next = [...rows];
                  next[index] = {
                    ...(typeof next[index] === 'string' ? {} : next[index]),
                    imageUrl: v,
                  };
                  updateRows(next);
                }}
                className="full"
              />
            </div>
          </div>
        )})}

        <div className="admin-actions">
          <button
            type="button"
            className="admin-btn primary"
            onClick={() => updateRows([...(rows || []), { imageUrl: '' }])}
          >
            Yeni Logo Ekle
          </button>
        </div>
      </>
    );
  };

  const renderProductCategoriesTab = () => {
    const activeCategory = productCategoryConfigs.find((item) => item.key === productCategoryAdminTab) || productCategoryConfigs[0];
    const rows = getCategoryProducts(activeCategory.key);
    const editorCategory = productCategoryConfigs.find((item) => item.key === productEditor.categoryKey) || activeCategory;
    const productEditorImageRows = Array.isArray(productEditor.draft.imageUrls) && productEditor.draft.imageUrls.length
      ? productEditor.draft.imageUrls
      : [''];

    return (
      <>
        <div className="admin-section admin-products-hero">
          <h3>Ürün Kategorileri</h3>
          <div className="admin-hint">
            Ürünleri kart görünümünde takip edebilirsiniz. Kartlardaki Yukarı/Aşağı butonlarıyla sıralamayı değiştirebilirsiniz.
          </div>
        </div>

        <div className="admin-section">
          <div className="admin-subtabs admin-category-tabs">
            {productCategoryConfigs.map((category) => (
              <button
                key={`category-tab-${category.key}`}
                type="button"
                className={`admin-subtab admin-category-tab admin-category-tab-${category.key} ${productCategoryAdminTab === category.key ? 'active' : ''}`.trim()}
                onClick={() => setProductCategoryAdminTab(category.key)}
              >
                {category.title}
              </button>
            ))}
          </div>
        </div>

        <div className={`admin-section admin-repeat-card admin-category-card admin-category-card-${activeCategory.key}`}>
          <div className="admin-repeat-head">
            <h3>{activeCategory.title}</h3>
            <div className="admin-actions">
              <button
                type="button"
                className="admin-btn primary admin-category-add-btn"
                onClick={() => openNewProductModal(activeCategory.key)}
              >
                Yeni Ürün Ekle
              </button>
            </div>
          </div>

          <div className="admin-form-grid" style={{ marginBottom: 14 }}>
            <TextInput
              label={`${activeCategory.title} Banner Görsel URL`}
              value={getByPath(content, `productCategories.${activeCategory.key}.bannerImageUrl`, '')}
              onChange={(v) => setPath(`productCategories.${activeCategory.key}.bannerImageUrl`, v)}
              className="full"
            />
            <FileToBase64Field
              label={`${activeCategory.title} Banner Görsel Yükle`}
              onSelect={(v) => setPath(`productCategories.${activeCategory.key}.bannerImageUrl`, v)}
              className="full"
            />
          </div>

          {rows.length ? (
            <div className="admin-product-grid">
              {rows.map((product, index) => {
                const description = product.description || '';
                const shortDescription = description.length > 160 ? `${description.slice(0, 160)}...` : description;
                const previewImage = (Array.isArray(product.imageUrls) && product.imageUrls[0]) || product.imageUrl;
                return (
                  <div className="admin-product-card" key={`${activeCategory.key}-product-${index}`}>
                    <div className="admin-product-image-wrap">
                      {previewImage ? (
                        <img src={previewImage} alt={product.name || `Ürün ${index + 1}`} className="admin-product-image" />
                      ) : (
                        <div className="admin-product-image-fallback">Görsel Yok</div>
                      )}
                    </div>
                    <div className="admin-product-card-body">
                      <h4>{product.name || `Ürün ${index + 1}`}</h4>
                      <div className="admin-product-code">{product.code || 'Kod Girilmedi'}</div>
                      <div className="admin-product-order-toolbar">
                        <div className="admin-product-order-pill">
                          <span className="admin-product-order-label">Liste Sırası</span>
                          <strong>{index + 1}</strong>
                        </div>
                        <div className="admin-product-order-controls">
                          <button
                            type="button"
                            className="admin-order-btn"
                            onClick={() => moveProductInCategory(activeCategory.key, index, index - 1)}
                            disabled={index === 0}
                            title="Ürünü yukarı taşı"
                            aria-label="Ürünü yukarı taşı"
                          >
                            <i className="fa fa-chevron-up" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="admin-order-btn"
                            onClick={() => moveProductInCategory(activeCategory.key, index, index + 1)}
                            disabled={index === rows.length - 1}
                            title="Ürünü aşağı taşı"
                            aria-label="Ürünü aşağı taşı"
                          >
                            <i className="fa fa-chevron-down" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                      <p>{shortDescription || 'Açıklama girilmedi.'}</p>
                      <div className="admin-actions admin-product-main-actions">
                        <button
                          type="button"
                          className="admin-btn light admin-product-action-btn"
                          onClick={() => openEditProductModal(activeCategory.key, index)}
                        >
                          <i className="fa fa-pencil" aria-hidden="true" />
                          Düzenle
                        </button>
                        <button
                          type="button"
                          className="admin-btn danger admin-product-action-btn"
                          onClick={() => removeProductFromCategory(activeCategory.key, index)}
                        >
                          <i className="fa fa-trash" aria-hidden="true" />
                          Sil
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="admin-placeholder">
              Bu kategoride henüz ürün yok. Yukarıdaki <strong>Yeni Ürün Ekle</strong> butonu ile ürün oluşturabilirsiniz.
            </div>
          )}
        </div>

        {productEditor.isOpen ? (
          <div className="admin-modal-backdrop" onClick={closeProductModal}>
            <div className="admin-modal-card" onClick={(event) => event.stopPropagation()}>
              <div className="admin-modal-head">
                <h3>
                  {productEditor.mode === 'edit' ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'} - {editorCategory.title}
                </h3>
                <button type="button" className="admin-btn light" onClick={closeProductModal}>
                  Kapat
                </button>
              </div>

              <div className="admin-form-grid">
                {productEditorImageRows.map((imageUrl, imageIndex) => (
                  <div className="admin-section admin-repeat-card full" key={`product-editor-image-${imageIndex}`}>
                    <div className="admin-repeat-head">
                      <h3>Ürün Görseli {imageIndex + 1}</h3>
                      <div className="admin-actions">
                        <button
                          type="button"
                          className="admin-btn light"
                          onClick={() => removeProductEditorImageRow(imageIndex)}
                          disabled={productEditorImageRows.length <= 1}
                        >
                          Görseli Sil
                        </button>
                      </div>
                    </div>
                    <div className="admin-form-grid">
                      <TextInput
                        label="Görsel URL"
                        value={imageUrl}
                        onChange={(v) => setProductEditorImageAt(imageIndex, v)}
                        className="full"
                      />
                      <FileToBase64Field
                        label="Görsel Yükle"
                        onSelect={(v) => setProductEditorImageAt(imageIndex, v)}
                        className="full"
                      />
                    </div>
                  </div>
                ))}

                <div className="admin-actions full">
                  <button type="button" className="admin-btn primary" onClick={addProductEditorImageRow}>
                    Yeni Görsel Ekle
                  </button>
                </div>
                <TextInput
                  label="Ürün İsmi"
                  value={productEditor.draft.name}
                  onChange={(v) => setProductEditorField('name', v)}
                  className="full"
                />
                <TextInput
                  label="Ürün Kodu"
                  value={productEditor.draft.code}
                  onChange={(v) => setProductEditorField('code', v)}
                />
                <TextArea
                  label="Açıklama Metni"
                  value={productEditor.draft.description}
                  onChange={(v) => setProductEditorField('description', v)}
                  className="full"
                />
              </div>

              <div className="admin-actions admin-modal-actions">
                <button type="button" className="admin-btn light" onClick={closeProductModal}>
                  Vazgeç
                </button>
                <button type="button" className="admin-btn primary" onClick={saveProductModal}>
                  {productEditor.mode === 'edit' ? 'Değişiklikleri Kaydet' : 'Ürünü Ekle'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </>
    );
  };

  const renderAboutTab = () => (
    <>
      <div className="admin-section">
        <div className="admin-subtabs">
          <button type="button" className={`admin-subtab ${aboutAdminTab === 'general' ? 'active' : ''}`} onClick={() => setAboutAdminTab('general')}>Genel</button>
          <button type="button" className={`admin-subtab ${aboutAdminTab === 'whatwedo' ? 'active' : ''}`} onClick={() => setAboutAdminTab('whatwedo')}>Neler Yapıyoruz</button>
          <button type="button" className={`admin-subtab ${aboutAdminTab === 'documents' ? 'active' : ''}`} onClick={() => setAboutAdminTab('documents')}>Belgelerimiz</button>
          <button type="button" className={`admin-subtab ${aboutAdminTab === 'services' ? 'active' : ''}`} onClick={() => setAboutAdminTab('services')}>Tüm Hizmetler</button>
          <button type="button" className={`admin-subtab ${aboutAdminTab === 'projects' ? 'active' : ''}`} onClick={() => setAboutAdminTab('projects')}>Referanslarımız</button>
        </div>
      </div>

      {aboutAdminTab === 'general' ? renderAboutGeneralTab() : null}
      {aboutAdminTab === 'whatwedo' ? renderAboutWhatWeDoTab() : null}
      {aboutAdminTab === 'documents' ? renderAboutDocumentsTab() : null}
      {aboutAdminTab === 'services' ? renderAboutServicesTab() : null}
      {aboutAdminTab === 'projects' ? renderAboutProjectsTab() : null}
    </>
  );

  const renderNumbersTab = () => (
    <>
      <div className="admin-section">
        <h3>Rakamlarla Biz</h3>
        <div className="admin-hint">Ana sayfadaki sol blokta kullanılır.</div>
        <div className="admin-form-grid">
          <TextInput label="Bölüm Başlığı" value={getLang('pages.home.numbers.title', '')} onChange={(v) => setLang('pages.home.numbers.title', v)} className="full" />
          <TextInput label="Arka Plan Görsel URL" value={getByPath(content, 'media.pages.homeNumbersBackground', '')} onChange={(v) => setPath('media.pages.homeNumbersBackground', v)} className="full" />
          <FileToBase64Field label="Arka Plan Görsel Yükle" onSelect={(v) => setPath('media.pages.homeNumbersBackground', v)} className="full" />
        </div>
      </div>

      {Array.from({ length: 4 }).map((_, index) => (
        <div className="admin-section admin-repeat-card" key={`numbers-item-${index}`}>
          <div className="admin-repeat-head">
            <h3>Rakam Kartı {index + 1}</h3>
          </div>
          <div className="admin-form-grid">
            <TextInput label="Değer" value={getLang(`pages.home.numbers.items.${index}.value`, '')} onChange={(v) => setLang(`pages.home.numbers.items.${index}.value`, v)} />
            <TextInput label="Suffix (örn: K)" value={getLang(`pages.home.numbers.items.${index}.suffix`, '')} onChange={(v) => setLang(`pages.home.numbers.items.${index}.suffix`, v)} />
            <TextInput label="Başlık" value={getLang(`pages.home.numbers.items.${index}.label`, '')} onChange={(v) => setLang(`pages.home.numbers.items.${index}.label`, v)} className="full" />
          </div>
        </div>
      ))}
    </>
  );

  const renderTestimonialsTab = () => (
    <>
      <div className="admin-section">
        <h3>Müşteri Yorumları</h3>
        <div className="admin-hint">Ana sayfa ve Hakkımızda sayfasındaki yorum alanlarında ortak kullanılır.</div>
        <div className="admin-form-grid">
          <TextInput label="Bölüm Başlığı" value={getLang('pages.home.testimonials.title', '')} onChange={(v) => setLang('pages.home.testimonials.title', v)} className="full" />
        </div>
      </div>

      {Array.from({ length: 5 }).map((_, index) => (
        <div className="admin-section admin-repeat-card" key={`testimonial-item-${index}`}>
          <div className="admin-repeat-head">
            <h3>Yorum {index + 1}</h3>
          </div>
          <div className="admin-form-grid">
            <TextInput label="Görsel URL" value={getByPath(content, `media.pages.testimonialPeople.${index}.imageUrl`, '')} onChange={(v) => setPath(`media.pages.testimonialPeople.${index}.imageUrl`, v)} className="full" />
            <FileToBase64Field label="Görsel Yükle" onSelect={(v) => setPath(`media.pages.testimonialPeople.${index}.imageUrl`, v)} className="full" />
            <TextInput label="İsim" value={getLang(`pages.home.testimonials.items.${index}.name`, '')} onChange={(v) => setLang(`pages.home.testimonials.items.${index}.name`, v)} />
            <TextInput label="Ünvan / Rol" value={getLang(`pages.home.testimonials.items.${index}.role`, '')} onChange={(v) => setLang(`pages.home.testimonials.items.${index}.role`, v)} />
            <TextArea label="Yorum Metni" value={getLang(`pages.home.testimonials.items.${index}.quote`, '')} onChange={(v) => setLang(`pages.home.testimonials.items.${index}.quote`, v)} className="full" />
          </div>
        </div>
      ))}
    </>
  );

  const renderClientsTab = () => (
    (() => {
      const logos = Array.isArray(getByPath(content, 'media.pages.clientLogos', []))
        ? getByPath(content, 'media.pages.clientLogos', [])
        : [];

      const rows = logos.length ? logos : [''];

      const updateRows = (nextRows) => {
        setPath(
          'media.pages.clientLogos',
          nextRows.map((item) => (typeof item === 'string' ? item : item?.imageUrl || ''))
        );
      };

      return (
        <>
          <div className="admin-section">
            <h3>Müşterilerimiz Logoları</h3>
            <div className="admin-hint">Ana sayfa ve Hakkımızda sayfasında ortak kullanılır. Sadece logo görselleri gösterilir.</div>
          </div>

          {rows.map((item, index) => {
            const value = typeof item === 'string' ? item : item?.imageUrl || '';
            return (
              <div className="admin-section admin-repeat-card" key={`client-logo-${index}`}>
                <div className="admin-repeat-head">
                  <h3>Logo {index + 1}</h3>
                  <div className="admin-actions">
                    <button
                      type="button"
                      className="admin-btn light"
                      onClick={() => {
                        if (rows.length <= 1) return;
                        updateRows(rows.filter((_, rowIndex) => rowIndex !== index));
                      }}
                      disabled={rows.length <= 1}
                    >
                      Logoyu Sil
                    </button>
                  </div>
                </div>
                <div className="admin-form-grid">
                  <TextInput
                    label="Logo URL"
                    value={value}
                    onChange={(v) => {
                      const next = [...rows];
                      next[index] = v;
                      updateRows(next);
                    }}
                    className="full"
                  />
                  <FileToBase64Field
                    label="Logo Yükle"
                    onSelect={(v) => {
                      const next = [...rows];
                      next[index] = v;
                      updateRows(next);
                    }}
                    className="full"
                  />
                </div>
              </div>
            );
          })}

          <div className="admin-actions">
            <button
              type="button"
              className="admin-btn primary"
              onClick={() => updateRows([...(logos.length ? logos : rows), ''])}
            >
              Yeni Logo Ekle
            </button>
          </div>
        </>
      );
    })()
  );

  const renderFormLeadsTab = () => {
    const formatDate = (isoValue) => {
      if (!isoValue) return '-';
      const date = new Date(isoValue);
      if (Number.isNaN(date.getTime())) return String(isoValue);
      return date.toLocaleString('tr-TR');
    };

    const handleDeleteLead = async (id) => {
      if (!id) return;
      const shouldDelete = window.confirm('Bu form kaydını silmek istediğinize emin misiniz?');
      if (!shouldDelete) return;

      try {
        await deleteFormSubmission(id);
        await loadFormLeads();
        setMessage('Form kaydı silindi.');
      } catch (error) {
        setMessage(error?.message || 'Form kaydı silinemedi.');
      }
    };

    const handleClearLeads = async () => {
      const shouldClear = window.confirm('Tüm form kayıtlarını silmek istediğinize emin misiniz?');
      if (!shouldClear) return;

      try {
        await clearFormSubmissions();
        await loadFormLeads();
        setMessage('Tüm form kayıtları temizlendi.');
      } catch (error) {
        setMessage(error?.message || 'Form kayıtları temizlenemedi.');
      }
    };

    return (
      <>
        <div className="admin-section">
          <h3>Form Talepleri</h3>
          <div className="admin-hint">İletişim sayfası ve header popup formundan gelen kayıtlar burada listelenir.</div>
          <div className="admin-actions">
            <button type="button" className="admin-btn light" onClick={loadFormLeads} disabled={isFormLeadsLoading}>
              {isFormLeadsLoading ? 'Yükleniyor...' : 'Yenile'}
            </button>
            <button type="button" className="admin-btn danger" onClick={handleClearLeads} disabled={isFormLeadsLoading || !formLeads.length}>
              Tümünü Sil
            </button>
          </div>
          {formLeadsError ? <div className="admin-error">{formLeadsError}</div> : null}
        </div>

        {isFormLeadsLoading ? (
          <div className="admin-section">
            <div className="admin-hint">Kayıtlar yükleniyor...</div>
          </div>
        ) : null}

        {!isFormLeadsLoading && !formLeads.length ? (
          <div className="admin-section">
            <div className="admin-placeholder">Henüz form kaydı yok.</div>
          </div>
        ) : null}

        {!isFormLeadsLoading && formLeads.length ? (
          formLeads.map((item, index) => (
            <div className="admin-section admin-repeat-card" key={item.id || `form-lead-${index}`}>
              <div className="admin-repeat-head">
                <h3>{item.name || `Kayıt ${index + 1}`}</h3>
                <div className="admin-actions">
                  <button type="button" className="admin-btn danger" onClick={() => handleDeleteLead(item.id)}>
                    Sil
                  </button>
                </div>
              </div>
              <div className="admin-form-grid">
                <Field label="Gönderim Tarihi" className="full">
                  <input type="text" value={formatDate(item.createdAt)} readOnly />
                </Field>
                <Field label="Kaynak">
                  <input type="text" value={item.source || '-'} readOnly />
                </Field>
                <Field label="Sayfa">
                  <input type="text" value={item.page || '-'} readOnly />
                </Field>
                <Field label="Ad Soyad">
                  <input type="text" value={item.name || ''} readOnly />
                </Field>
                <Field label="E-posta">
                  <input type="text" value={item.email || ''} readOnly />
                </Field>
                <Field label="Telefon">
                  <input type="text" value={item.phone || ''} readOnly />
                </Field>
                <Field label="Mesaj" className="full">
                  <textarea value={item.message || ''} readOnly />
                </Field>
              </div>
            </div>
          ))
        ) : null}
      </>
    );
  };

  const renderContactTab = () => (
    <>
      <div className="admin-section">
        <h3>Ortak İletişim Verileri (Header / Footer / İletişim Sayfası)</h3>
        <div className="admin-form-grid">
          <TextInput label="E-posta" value={getByPath(content, 'contact.email', '')} onChange={(v) => setPath('contact.email', v)} className="full" />
          <TextInput label="Facebook URL" value={getByPath(content, 'social.facebook', '')} onChange={(v) => setPath('social.facebook', v)} />
          <TextInput label="Instagram URL" value={getByPath(content, 'social.instagram', '')} onChange={(v) => setPath('social.instagram', v)} />
          <TextInput label="Çalışma Saatleri (Hafta içi/Cumartesi)" value={getByPath(content, 'contact.workingHours.weekdays', '')} onChange={(v) => setPath('contact.workingHours.weekdays', v)} className="full" />
          <TextInput label="Çalışma Saatleri (Pazar)" value={getByPath(content, 'contact.workingHours.sunday', '')} onChange={(v) => setPath('contact.workingHours.sunday', v)} className="full" />
          <TextInput label="Harita Lat" type="number" value={String(getByPath(content, 'contact.map.lat', ''))} onChange={(v) => setPath('contact.map.lat', Number(v))} />
          <TextInput label="Harita Lng" type="number" value={String(getByPath(content, 'contact.map.lng', ''))} onChange={(v) => setPath('contact.map.lng', Number(v))} />
          <TextInput label="Harita Zoom" type="number" value={String(getByPath(content, 'contact.map.zoom', ''))} onChange={(v) => setPath('contact.map.zoom', Number(v))} />
        </div>
      </div>

      <StringListEditor label="Adres Satırı" values={getByPath(content, 'contact.addressLines', [])} onChange={(v) => setPath('contact.addressLines', v)} />
      <StringListEditor label="Telefon" values={getByPath(content, 'contact.phones', [])} onChange={(v) => setPath('contact.phones', v)} />

      <div className="admin-section">
        <h3>Header İletişim Metinleri</h3>
        <div className="admin-form-grid">
          <TextInput label="Üst Bar E-posta Label" value={getLang('header.topBarEmailLabel', '')} onChange={(v) => setLang('header.topBarEmailLabel', v)} />
          <TextInput label="Üst Bar Telefon Label" value={getLang('header.topBarPhoneLabel', '')} onChange={(v) => setLang('header.topBarPhoneLabel', v)} />
          <TextInput label="Header Katalog Menü Yazısı" value={getLang('header.catalogLabel', '')} onChange={(v) => setLang('header.catalogLabel', v)} />
          <TextInput label="Footer Katalog Link Yazısı" value={getLang('footer.catalogLabel', '')} onChange={(v) => setLang('footer.catalogLabel', v)} />
          <TextInput label="Header Referanslar Menü Yazısı" value={getLang('header.referencesLabel', '')} onChange={(v) => setLang('header.referencesLabel', v)} />
          <TextInput label="Footer Referanslar Link Yazısı" value={getLang('footer.referencesLabel', '')} onChange={(v) => setLang('footer.referencesLabel', v)} />
          <TextInput label="Katalog PDF URL" value={getByPath(content, 'media.catalogPdfUrl', '')} onChange={(v) => setPath('media.catalogPdfUrl', v)} className="full" />
          <FileToBase64Field label="Katalog PDF Yükle" accept=".pdf,application/pdf" onSelect={(v) => setPath('media.catalogPdfUrl', v)} className="full" />
          <TextInput label="Referanslar PDF URL" value={getByPath(content, 'media.referencesPdfUrl', '')} onChange={(v) => setPath('media.referencesPdfUrl', v)} className="full" />
          <FileToBase64Field label="Referanslar PDF Yükle" accept=".pdf,application/pdf" onSelect={(v) => setPath('media.referencesPdfUrl', v)} className="full" />
          <TextInput label="Popup Buton Yazısı" value={getLang('header.contactPopupButton', '')} onChange={(v) => setLang('header.contactPopupButton', v)} />
          <TextInput label="Arama Placeholder" value={getLang('header.searchPlaceholder', '')} onChange={(v) => setLang('header.searchPlaceholder', v)} />
          <TextInput label="Popup Telefon Başlık" value={getLang('header.popup.phoneTitle', '')} onChange={(v) => setLang('header.popup.phoneTitle', v)} />
          <TextInput label="Popup E-posta Başlık" value={getLang('header.popup.emailTitle', '')} onChange={(v) => setLang('header.popup.emailTitle', v)} />
          <TextInput label="Popup Adres Başlık" value={getLang('header.popup.addressTitle', '')} onChange={(v) => setLang('header.popup.addressTitle', v)} />
          <TextInput label="Popup Çalışma Saatleri Başlık" value={getLang('header.popup.workingHoursTitle', '')} onChange={(v) => setLang('header.popup.workingHoursTitle', v)} className="full" />
        </div>
      </div>

      <div className="admin-section">
        <h3>İletişim Sayfası Metinleri</h3>
        <div className="admin-form-grid">
          <TextInput label="Banner Başlık" value={getLang('pages.contact.banner.title', '')} onChange={(v) => setLang('pages.contact.banner.title', v)} />
          <TextInput label="Breadcrumb Başlığı" value={getLang('pages.contact.banner.pageName', '')} onChange={(v) => setLang('pages.contact.banner.pageName', v)} />
          <TextArea label="Banner Açıklama" value={getLang('pages.contact.banner.description', '')} onChange={(v) => setLang('pages.contact.banner.description', v)} className="full" />
          <TextInput label="Form Başlığı" value={getLang('pages.contact.form.title', '')} onChange={(v) => setLang('pages.contact.form.title', v)} />
          <TextInput label="Form Gönder Butonu" value={getLang('pages.contact.form.submitLabel', '')} onChange={(v) => setLang('pages.contact.form.submitLabel', v)} />
          <TextInput label="Form Alanı - Ad Soyad" value={getLang('pages.contact.form.fields.name', '')} onChange={(v) => setLang('pages.contact.form.fields.name', v)} />
          <TextInput label="Form Alanı - E-posta" value={getLang('pages.contact.form.fields.email', '')} onChange={(v) => setLang('pages.contact.form.fields.email', v)} />
          <TextInput label="Form Alanı - Telefon" value={getLang('pages.contact.form.fields.phone', '')} onChange={(v) => setLang('pages.contact.form.fields.phone', v)} />
          <TextInput label="Form Alanı - Mesaj" value={getLang('pages.contact.form.fields.message', '')} onChange={(v) => setLang('pages.contact.form.fields.message', v)} />
          <TextInput label="Bilgi Kartı Başlığı" value={getLang('pages.contact.info.title', '')} onChange={(v) => setLang('pages.contact.info.title', v)} />
          <TextInput label="Adres Başlığı" value={getLang('pages.contact.info.addressTitle', '')} onChange={(v) => setLang('pages.contact.info.addressTitle', v)} />
          <TextInput label="Çalışma Saatleri Başlığı" value={getLang('pages.contact.info.workingHoursTitle', '')} onChange={(v) => setLang('pages.contact.info.workingHoursTitle', v)} />
          <TextInput label="E-Posta Başlığı" value={getLang('pages.contact.info.emailTitle', '')} onChange={(v) => setLang('pages.contact.info.emailTitle', v)} />
          <TextInput label="Telefon Başlığı" value={getLang('pages.contact.info.phoneTitle', '')} onChange={(v) => setLang('pages.contact.info.phoneTitle', v)} />
          <TextInput label="Telefon Prefix" value={getLang('pages.contact.info.phonePrefix', '')} onChange={(v) => setLang('pages.contact.info.phonePrefix', v)} />
          <TextInput label="İletişim Banner Görseli URL" value={getByPath(content, 'media.pages.contactBanner', '')} onChange={(v) => setPath('media.pages.contactBanner', v)} className="full" />
          <FileToBase64Field label="İletişim Banner Görseli Yükle" onSelect={(v) => setPath('media.pages.contactBanner', v)} className="full" />
        </div>
      </div>
    </>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'slider':
        return renderSliderTab();
      case 'about':
        return renderAboutTab();
      case 'productCategories':
        return renderProductCategoriesTab();
      case 'numbers':
        return renderNumbersTab();
      case 'testimonials':
        return renderTestimonialsTab();
      case 'clients':
        return renderClientsTab();
      case 'contact':
        return renderContactTab();
      case 'formLeads':
        return renderFormLeadsTab();
      default:
        return null;
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-shell">
        <div className="admin-topline">
          <div>
            <h1 className="admin-title">Yönetim Paneli</h1>
            <div className="admin-subtitle">
              {isBlobSyncEnabled
                ? 'Bölüm bazlı içerik yönetimi (bulut + localStorage)'
                : 'Bölüm bazlı içerik yönetimi (localStorage + static JSON)'}
            </div>
            <div className="admin-hint">Dosya yükleme (görsel/PDF) `/api/upload` endpoint&apos;i üzerinden çalışır (Vercel uyumlu).</div>
            {!isBlobSyncEnabled ? (
              <div className="admin-hint">Kalici cihazlar arasi senkron icin Vercel ortam degiskeni `BLOB_READ_WRITE_TOKEN` eklenmeli.</div>
            ) : null}
            {message ? <div className="admin-hint" style={{ marginTop: 6 }}>{message}</div> : null}
          </div>
          <div className="admin-actions">
            <Link to="/" className="admin-btn light" style={{ textDecoration: 'none' }}>Siteyi Aç</Link>
            <button type="button" className="admin-btn light" onClick={() => setShowJsonTools((v) => !v)}>
              {showJsonTools ? 'JSON Araçlarını Gizle' : 'JSON Araçlarını Göster'}
            </button>
            <button type="button" className="admin-btn danger" onClick={handleLogout}>Çıkış Yap</button>
          </div>
        </div>

        <div className="admin-grid">
          <div className="admin-card admin-side">
            <div className="admin-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`admin-tab ${tab.id === 'productCategories' ? 'admin-tab-products' : ''} ${activeTab === tab.id ? 'active' : ''}`.trim()}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="admin-card admin-content-card">
              {renderTabContent()}
            </div>

            {showJsonTools ? (
              <div className="admin-card" style={{ marginTop: 16 }}>
                <div className="admin-section" style={{ marginBottom: 0 }}>
                  <h3>JSON Araçları</h3>
                  <div className="admin-hint">Toplu içe/dışa aktarma ve varsayılanlara dönme işlemleri.</div>
                  <textarea className="admin-json" value={jsonDraft} onChange={(e) => setJsonDraft(e.target.value)} />
                  <div className="admin-actions">
                    <button type="button" className="admin-btn primary" onClick={handleImport}>JSON İçe Aktar</button>
                    <button type="button" className="admin-btn secondary" onClick={handleExport}>JSON Dışa Aktar</button>
                    <button type="button" className="admin-btn danger" onClick={handleReset}>Varsayılana Sıfırla</button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
